import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import QRCodeImage from "../assets/QRCodeImage.png";
import qrDialogBackground from "../assets/qr-dialog-background.png";
import attachment from "../assets/attachment.png";
import MobileBottomSheet from "./MobileBottomSheet";

const DialogOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 99;

  @media (max-width: 768px) {
    display: none; /* Hide desktop overlay on mobile */
  }
`;

const Dialog = styled(motion.div)`
  position: fixed;
  top: 45%;
  left: 45%;
  transform: translate(-45%, -45%) rotate(355deg);
  background: white url(${qrDialogBackground}) no-repeat center center;
  background-size: cover;
  padding: 3rem;
  border-radius: 24px;
  z-index: 100;
  width: 800px;
  height: 460px;
  max-width: calc(90vw - 64px);
  margin-bottom: 40px;
  margin-right: 40px;
  display: flex;
  align-items: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    display: none; /* Hide desktop dialog on mobile */
  }
`;

const QRCodeImageContainer = styled.img`
  position: absolute;
  right: 20px;
  top: -5%;
  transform: rotate(5deg);
  width: 220px;
  border-radius: 10px;
  height: auto;
  z-index: 1;
`;

const DialogContent = styled.div`
  max-width: 55%;
  position: relative;
  z-index: 2;
  transform: rotate(5deg);
  padding-left: 2rem;
  margin-top: 40px;

  h2 {
    font-size: 36px;
    font-weight: 900;
    margin-bottom: 1.5rem;
    font-style: italic;
    color: #000;
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-family: "Inter", sans-serif;
  }

  p {
    font-size: 16px;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    color: rgba(0, 0, 0, 0.8);
    font-weight: 500;
    transform: rotate(1deg);
    margin-left: 1rem;
    font-family: "Inter", sans-serif;
  }
`;

const AttachmentImage = styled.img`
  position: absolute;
  left: 24px;
  top: 24px;
  width: 24px;
  height: auto;
  opacity: 0.4;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  width: 48px;
  height: 48px;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  z-index: 101; // Increased z-index to appear above Dialog
  transform: translate(-100%, -100%); // Move button outside the dialog
`;

// Update the component render section with proper structure
const QRCodeDialog = ({ isOpen, onClose }) => {
  const [isMobile, setIsMobile] = useState(() => {
    // Initialize with correct value to prevent hydration mismatch
    return typeof window !== "undefined" ? window.innerWidth <= 768 : false;
  });

  useEffect(() => {
    const checkMobile = () => {
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(prev => (prev !== newIsMobile ? newIsMobile : prev));
    };

    // Only add listener if needed
    let timeoutId;
    const debouncedCheckMobile = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkMobile, 100);
    };

    window.addEventListener("resize", debouncedCheckMobile);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", debouncedCheckMobile);
    };
  }, []);

  // Memoize mobile bottom sheet to prevent unnecessary re-renders
  const mobileBottomSheet = useMemo(
    () => isMobile && <MobileBottomSheet isOpen={isOpen} onClose={onClose} />,
    [isMobile, isOpen, onClose]
  );

  return (
    <>
      {/* Desktop Dialog */}
      <AnimatePresence>
        {isOpen && !isMobile && (
          <>
            <DialogOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
            />
            <Dialog
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              <AttachmentImage src={attachment} alt="" />
              <CloseButton onClick={onClose}>×</CloseButton>
              <DialogContent>
                <h2>SCAN TO DOWNLOAD THE PLUM APP</h2>
                <p>
                  NOW THAT YOU'VE EXPLORED THE 42 REASONS, IT'S TIME TO DOWNLOAD
                  THE APP AND EXPERIENCE THE BENEFITS FOR YOURSELF. JUST LIKE
                  THE MYSTERIOUS NUMBER 42 IN THE HITCHHIKER'S GUIDE TO THE
                  GALAXY, OUR APP HOLDS THE KEY TO UNLOCKING A WORLD OF
                  POSSIBILITIES. DON'T MISS OUT ON WHAT COULD BE THE ULTIMATE
                  SOLUTION FOR YOUR NEEDS!
                </p>
              </DialogContent>
              <QRCodeImageContainer src={QRCodeImage} alt="QR Code" />
            </Dialog>
          </>
        )}
      </AnimatePresence>

      {/* Mobile Bottom Sheet */}
      {mobileBottomSheet}
    </>
  );
};

export default QRCodeDialog;
